// Shared styling for filter panels (task/report/document/user filters, droprealms).
// Emotion `styled` components handle layout that varies by props (e.g. isMobile);
// plain `sx` objects cover one-off styling applied directly to MUI components.
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

/** Outer wrapper for a mobile "Filters" toggle button + chips row. */
export const FilterRow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: isMobile ? 'flex-start' : 'center',
  // Matches the former `.filterRow .FilterBtn` rule: space out the first
  // action button (e.g. the "Filters" toggle) from whatever follows it.
  '& > .MuiButtonBase-root:first-of-type': {
    marginRight: theme.spacing(1),
  },
}));

/** Wraps a group of toggle/segment controls that should wrap onto multiple lines. */
export const FlexFilters = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  maxWidth: 560,
  justifyContent: 'space-around',
  alignItems: isMobile ? 'center' : 'flex-start',
}));

/** ToggleButtonGroup styled to look like a pill-shaped filter control. */
export const FilterBtnGroup = styled(ToggleButtonGroup)({
  margin: '5px 0',
  backgroundColor: 'transparent',
  '& > button': {
    borderRadius: 100,
    padding: '0px 15px',
  },
});

export const filterGridSx = {
  maxWidth: 'md',
  p: 2,
  margin: 'auto',
  displayPrint: 'none',
};

export const filterFormGridSx = {
  p: 1,
  width: '100%',
  minWidth: 200,
  alignItems: 'flex-start',
  margin: 'auto',
};

/** "Filters" title above the field list. Inset matches filterFormGridSx so it shares the same left edge. */
export const FilterPanelHeader = styled(Typography)(({ theme }) => ({
  display: 'block',
  width: '100%',
  textAlign: 'left',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));
FilterPanelHeader.defaultProps = { variant: 'overline', component: 'div' };

export const filterFormItemSx = { width: '100%' };

export const filterFormLabelSx = { fontSize: '0.75rem' };

export const filterIconSx = { mr: 1 };

export const filterCheckRowSx = { display: 'flex', flexDirection: 'row' };

export const filterCheckIconSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.primary',
  mr: 1,
  '& svg': { color: 'inherit', fill: 'currentColor' },
};

export const filterLinkTextSx = {
  cursor: 'pointer',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  minWidth: 'auto',
};

export const filterCompareRowSx = { display: 'flex', flexDirection: 'row', width: '100%' };

export const filterCompareCloseSx = { height: 35, position: 'relative', top: 7 };

export const filterFormControlPaddingSx = {
  '& label + .MuiInput-formControl': { mt: 0.5 },
};

export const filterDatePickerPaddingSx = {
  width: '100%',
  mt: 1,
  '& label + .MuiInput-formControl': { mt: 2 },
};

export const filterHiddenSx = { display: 'none' };
